import {Box, Stack} from '@dropbox/dig-foundations';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {useAtom} from 'jotai';
import {useMemo} from 'react';
import {logDashboardInteraction} from 'views/home/dashboard/dashboardAnalytics';
import {Tile} from 'views/home/dashboard/Tile';
import {TileListSkeleton} from 'views/home/dashboard/tiles/TileListSkeleton';

import {CalendarList} from './calendar/CalendarList';
import {CalendarMonthNav} from './calendar/CalendarMonthNav';
import {calendarPeopleFilterAtom} from './calendar/calendarPeopleFilterAtom';
import {
  isCalendarPeopleGroupEnabled,
  toggleCalendarPeopleGroup,
} from './calendar/calendarPeopleGroups';
import {CalendarTileMenu} from './calendar/CalendarTileMenu';
import {CalendarTileView, calendarViewAtom} from './calendar/CalendarViewToggle';
import {MiniTeamCalendar} from './calendar/MiniTeamCalendar';
import {peopleOutInRange, recurringInRange} from './calendar/teamCalendar';
import {TeamCalendarSkeleton} from './calendar/TeamCalendarSkeleton';
import {useTeamCalendar} from './calendar/useTeamCalendar';
import {WeekStart, weekStartAtom} from './calendar/WeekStartToggle';

export const CalendarTile = () => {
  const {
    visible,
    label,
    days,
    overlapLayers,
    weekStartsOn,
    people,
    birthdays,
    anniversaries,
    isLoading,
    goToPrevMonth,
    goToNextMonth,
    goToToday,
    isOnToday,
    availablePeopleGroups,
  } = useTeamCalendar();
  const bodyRef = useSkeletonFadeIn(isLoading);
  // Remember the viewer's calendar/list choice across sessions (localStorage).
  const [view, setView] = useAtom(calendarViewAtom);
  const [weekStart, setWeekStart] = useAtom(weekStartAtom);
  const [peopleFilter, setPeopleFilter] = useAtom(calendarPeopleFilterAtom);

  const {rangeStart, rangeEnd} = visible;
  const rangePeople = useMemo(
    () => peopleOutInRange(rangeStart, rangeEnd, people),
    [rangeStart, rangeEnd, people]
  );
  const rangeBirthdays = useMemo(
    () => recurringInRange(rangeStart, rangeEnd, birthdays),
    [rangeStart, rangeEnd, birthdays]
  );
  const rangeAnniversaries = useMemo(
    () => recurringInRange(rangeStart, rangeEnd, anniversaries),
    [rangeStart, rangeEnd, anniversaries]
  );

  const handleViewChange = (next: CalendarTileView) => {
    logDashboardInteraction({
      action: 'calendar_view_change',
      tileId: 'calendar',
      value: next,
    });
    setView(next);
  };

  const handleWeekStartChange = (next: WeekStart) => {
    logDashboardInteraction({
      action: 'week_start_change',
      tileId: 'calendar',
      value: next === 1 ? 'monday' : 'sunday',
    });
    setWeekStart(next);
  };

  if (isLoading) {
    // Match the skeleton to the active view so it doesn't flash list rows while a
    // calendar grid is loading (and vice versa).
    return view === 'calendar' ? (
      <TeamCalendarSkeleton monthLabel={label} />
    ) : (
      <TileListSkeleton tileId="calendar" rows={4} label={label} />
    );
  }

  return (
    <Tile
      tileId="calendar"
      label={label}
      bodyRef={bodyRef}
      labelAccessory={
        <CalendarTileMenu
          view={view}
          onViewChange={handleViewChange}
          weekStartsOn={weekStart}
          onWeekStartChange={handleWeekStartChange}
          peopleFilter={peopleFilter}
          onMenuOpen={() =>
            logDashboardInteraction({action: 'calendar_menu_open', tileId: 'calendar'})
          }
          onTogglePeopleGroup={(group) => {
            const enabled = isCalendarPeopleGroupEnabled(peopleFilter, group);
            logDashboardInteraction({
              action: 'calendar_people_filter_change',
              tileId: 'calendar',
              target: group,
              value: enabled ? 'disable' : 'enable',
            });
            setPeopleFilter((prev) => toggleCalendarPeopleGroup(prev, group));
          }}
          availablePeopleGroups={availablePeopleGroups}
        />
      }
      accessory={
        <CalendarMonthNav
          onPrevMonth={() => {
            logDashboardInteraction({
              action: 'calendar_month_nav',
              tileId: 'calendar',
              value: 'prev',
            });
            goToPrevMonth();
          }}
          onNextMonth={() => {
            logDashboardInteraction({
              action: 'calendar_month_nav',
              tileId: 'calendar',
              value: 'next',
            });
            goToNextMonth();
          }}
          onJumpToUpcoming={
            isOnToday
              ? undefined
              : () => {
                  logDashboardInteraction({
                    action: 'calendar_month_nav',
                    tileId: 'calendar',
                    value: 'go_today',
                  });
                  goToToday();
                }
          }
        />
      }
    >
      <Stack gap="8" display="flex" height="100%">
        {view === 'calendar' ? (
          // Fill the leftover height; the calendar distributes it across its 6
          // week rows (grid-auto-rows: 1fr) so the last row never gets clipped.
          <Box style={{flex: 1, minHeight: 0}}>
            <MiniTeamCalendar
              days={days}
              overlapLayers={overlapLayers}
              weekStartsOn={weekStartsOn}
              onProfileLinkClick={(ldap) =>
                logDashboardInteraction({
                  action: 'facepile_click',
                  tileId: 'calendar',
                  target: ldap,
                  destination: 'profile',
                })
              }
            />
          </Box>
        ) : (
          // Let the list own the leftover height so the range scrolls inside the
          // tile instead of truncating to a "View all" footer.
          <Box style={{flex: 1, minHeight: 0}}>
            <CalendarList
              people={rangePeople}
              birthdays={rangeBirthdays}
              anniversaries={rangeAnniversaries}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
            />
          </Box>
        )}
      </Stack>
    </Tile>
  );
};
